import type { RelationType } from '#layers/data-model/types/relation';
import type { PrimitiveType, PrimitiveTypeName, ResolvedMonospaceSingleRelationField } from '#layers/data-model/types/schema';
import { BuiltinBlockIds } from 'virtual:monospace-builtin-block-ids';
import { callOrReturn } from '#layers/base';
import { FilterOperator } from '#layers/data-model/types/filters';
import { inferRelationType, isRelationToMonospaceFiles } from '#layers/data-model/utils/relation';
import { getTypename, getTypeParams } from '#layers/data-model/utils/schema/primitive-type';

/** Block id and default props resolved before field meta options are applied. */
export interface FieldPresentationBlock {
	block: string;
	props?: Record<string, unknown>;
}

/** Conservative filter defaults derived from a primitive schema type before template-specific filter config is applied. */
export interface PrimitiveFieldFilterDefaults {
	typeTemplateId?: string;
	/** Complete operator set accepted by the backend for this primitive type. */
	supportedOperators?: FilterOperator[];
	/** Regular Studio operator set shown before opting into advanced filtering. */
	defaultOperators?: FilterOperator[];
}

/** Conservative defaults derived from a primitive schema type before explicit field meta options are applied. */
export interface PrimitiveFieldPresentationDefaults {
	input: FieldPresentationBlock;
	display: FieldPresentationBlock;
	filter?: PrimitiveFieldFilterDefaults;
}

/** Presentation defaults derived from relation cardinality and target collection. */
export interface RelationFieldPresentationDefaults {
	input: FieldPresentationBlock;
	display: FieldPresentationBlock;
}

interface PrimitiveFieldConstants {
	/**
	 * Primitive defaults may be static or resolve against the full primitive type when
	 * type params materially affect block props.
	 */
	presentation:
		| PrimitiveFieldPresentationDefaults
		| ((type: PrimitiveType) => PrimitiveFieldPresentationDefaults);
}

type RelationTargetKind = 'file' | 'regular';

/** Generic display used by primitive types that do not need specialized formatting. */
const FORMATTED_VALUE_DISPLAY = {
	block: BuiltinBlockIds.DisplayFormattedValue,
} satisfies FieldPresentationBlock;

const EQUALITY_FILTER_OPERATORS = [
	FilterOperator.EQ,
	FilterOperator.NEQ,
] satisfies FilterOperator[];

const SET_FILTER_OPERATORS = [
	FilterOperator.IN,
	FilterOperator.NIN,
] satisfies FilterOperator[];

const NUMERIC_COMPARISON_FILTER_OPERATORS = [
	FilterOperator.GT,
	FilterOperator.GTE,
	FilterOperator.LT,
	FilterOperator.LTE,
] satisfies FilterOperator[];

const RANGE_FILTER_OPERATORS = [
	FilterOperator.BETWEEN,
	FilterOperator.NBETWEEN,
] satisfies FilterOperator[];

function filterDefaults(
	supportedOperators: readonly FilterOperator[],
	defaultOperators: readonly FilterOperator[] = supportedOperators,
): PrimitiveFieldFilterDefaults {
	return {
		supportedOperators: [...supportedOperators],
		defaultOperators: [...defaultOperators],
	};
}

/** Numeric primitives share the same input until a concrete type requires custom props. */
const NUMBER_PRESENTATION = {
	input: {
		block: BuiltinBlockIds.InputNumber,
	},
	display: FORMATTED_VALUE_DISPLAY,
	filter: filterDefaults([
		...EQUALITY_FILTER_OPERATORS,
		...NUMERIC_COMPARISON_FILTER_OPERATORS,
		...RANGE_FILTER_OPERATORS,
		...SET_FILTER_OPERATORS,
	]),
} satisfies PrimitiveFieldPresentationDefaults;

const STRING_MATCH_FILTER_OPERATORS = [
	FilterOperator.CONTAINS,
	FilterOperator.ICONTAINS,
	FilterOperator.NCONTAINS,
	FilterOperator.NICONTAINS,
] satisfies FilterOperator[];

const STRING_AFFIX_FILTER_OPERATORS = [
	FilterOperator.STARTS_WITH,
	FilterOperator.NSTARTS_WITH,
	FilterOperator.ENDS_WITH,
	FilterOperator.NENDS_WITH,
] satisfies FilterOperator[];

/** Engine-supported text operators include lexical comparisons and case-insensitive matching. */
const STRING_SUPPORTED_FILTER_OPERATORS = [
	FilterOperator.EQ,
	FilterOperator.NEQ,
	FilterOperator.LT,
	FilterOperator.LTE,
	FilterOperator.GT,
	FilterOperator.GTE,
	...STRING_MATCH_FILTER_OPERATORS,
	...STRING_AFFIX_FILTER_OPERATORS,
	...SET_FILTER_OPERATORS,
] satisfies FilterOperator[];

/** Regular text filtering prioritizes matching operators over lexical comparisons. */
const STRING_DEFAULT_FILTER_OPERATORS = [
	FilterOperator.CONTAINS,
	FilterOperator.NCONTAINS,
	...EQUALITY_FILTER_OPERATORS,
	...STRING_AFFIX_FILTER_OPERATORS,
	...SET_FILTER_OPERATORS,
] satisfies FilterOperator[];

/** UUID values only support exact-match style filters. */
const UUID_FILTER_OPERATORS = [
	...EQUALITY_FILTER_OPERATORS,
	...SET_FILTER_OPERATORS,
] satisfies FilterOperator[];

/** Boolean values only support equality filters. */
const BOOLEAN_FILTER_OPERATORS = EQUALITY_FILTER_OPERATORS;

/** Engine-supported JSON filters allow equality and inequality; regular Studio keeps equality as the default. */
const JSON_SUPPORTED_FILTER_OPERATORS = [
	FilterOperator.EQ,
	FilterOperator.NEQ,
] satisfies FilterOperator[];

const JSON_DEFAULT_FILTER_OPERATORS = [
	FilterOperator.EQ,
] satisfies FilterOperator[];

/**
 * Date-like primitives support ordering and range comparisons. Keep this on
 * the primitive type default so introspected date fields get correct filter
 * operators even when no explicit type template is configured.
 */
const DATETIME_FILTER_OPERATORS = [
	...EQUALITY_FILTER_OPERATORS,
	...NUMERIC_COMPARISON_FILTER_OPERATORS,
	...RANGE_FILTER_OPERATORS,
	...SET_FILTER_OPERATORS,
] satisfies FilterOperator[];

const DATE_FILTER_DEFAULTS = {
	typeTemplateId: 'datetime',
	...filterDefaults(DATETIME_FILTER_OPERATORS),
} satisfies PrimitiveFieldFilterDefaults;

/** Generic fallback for primitive types without specialized operator semantics. */
const FALLBACK_FILTER_OPERATORS = [
	...EQUALITY_FILTER_OPERATORS,
	...SET_FILTER_OPERATORS,
] satisfies FilterOperator[];

/** Primitive field defaults by schema type. Unlisted types use the fallback presentation. */
const PRIMITIVE_FIELD_CONSTANTS: Partial<
	Record<PrimitiveTypeName, PrimitiveFieldConstants>
> = {
	boolean: {
		presentation: {
			input: {
				block: BuiltinBlockIds.InputSwitch,
			},
			display: {
				block: BuiltinBlockIds.DisplayBooleanValue,
			},
			filter: filterDefaults(BOOLEAN_FILTER_OPERATORS),
		},
	},
	date: {
		presentation: {
			input: {
				block: BuiltinBlockIds.InputDate,
			},
			display: {
				block: BuiltinBlockIds.DisplayFormattedDate,
			},
			filter: DATE_FILTER_DEFAULTS,
		},
	},
	dateTime: {
		presentation: {
			input: {
				block: BuiltinBlockIds.InputDateTime,
			},
			display: {
				block: BuiltinBlockIds.DisplayFormattedDateTime,
			},
			filter: DATE_FILTER_DEFAULTS,
		},
	},
	dateTimeWithTimezone: {
		presentation: {
			input: {
				block: BuiltinBlockIds.InputDateTime,
			},
			display: {
				block: BuiltinBlockIds.DisplayFormattedDateTime,
			},
			filter: DATE_FILTER_DEFAULTS,
		},
	},
	decimal: {
		presentation: (type) => {
			const params = getTypeParams(type);

			return {
				input: {
					block: BuiltinBlockIds.InputDecimalNumber,
					...(params
						? { props: { ...params } as Record<string, unknown> }
						: {}),
				},
				display: FORMATTED_VALUE_DISPLAY,
				filter: NUMBER_PRESENTATION.filter,
			};
		},
	},
	float32: {
		presentation: NUMBER_PRESENTATION,
	},
	float64: {
		presentation: NUMBER_PRESENTATION,
	},
	int8: {
		presentation: NUMBER_PRESENTATION,
	},
	int16: {
		presentation: NUMBER_PRESENTATION,
	},
	int32: {
		presentation: NUMBER_PRESENTATION,
	},
	int64: {
		presentation: NUMBER_PRESENTATION,
	},
	json: {
		presentation: {
			input: {
				block: BuiltinBlockIds.InputCode,
			},
			display: {
				block: BuiltinBlockIds.DisplayJsonValue,
			},
			filter: filterDefaults(
				JSON_SUPPORTED_FILTER_OPERATORS,
				JSON_DEFAULT_FILTER_OPERATORS,
			),
		},
	},
	string: {
		presentation: {
			input: {
				block: BuiltinBlockIds.Input,
			},
			display: FORMATTED_VALUE_DISPLAY,
			filter: filterDefaults(
				STRING_SUPPORTED_FILTER_OPERATORS,
				STRING_DEFAULT_FILTER_OPERATORS,
			),
		},
	},
	time: {
		presentation: {
			input: {
				block: BuiltinBlockIds.InputTime,
			},
			display: {
				block: BuiltinBlockIds.DisplayFormattedTime,
			},
			filter: DATE_FILTER_DEFAULTS,
		},
	},
	uint8: {
		presentation: NUMBER_PRESENTATION,
	},
	uint16: {
		presentation: NUMBER_PRESENTATION,
	},
	uint32: {
		presentation: NUMBER_PRESENTATION,
	},
	uint64: {
		presentation: NUMBER_PRESENTATION,
	},
	uuid: {
		presentation: {
			input: {
				block: BuiltinBlockIds.InputUuid,
			},
			display: {
				block: BuiltinBlockIds.DisplayFormattedValue,
				props: {
					fontFamily: 'mono',
				},
			},
			filter: filterDefaults(UUID_FILTER_OPERATORS),
		},
	},
};

/** Fallback used by bytes, geometry, unsupported, and future primitive types. */
const FALLBACK_PRIMITIVE_FIELD_CONSTANTS = {
	presentation: {
		input: {
			block: BuiltinBlockIds.Input,
		},
		display: FORMATTED_VALUE_DISPLAY,
		filter: filterDefaults(FALLBACK_FILTER_OPERATORS),
	},
} satisfies PrimitiveFieldConstants;

/** Input block matrix keyed by target collection kind and inferred relation type. */
const RELATION_INPUT_BLOCKS = {
	file: {
		m2m: BuiltinBlockIds.InputFileM2M,
		m2o: BuiltinBlockIds.InputFileM2O,
		// o2m: Left explicitly unsupported on purpose:
		// resolving one signals a malformed schema and throws below.
		o2m: undefined,
		o2o: BuiltinBlockIds.InputFileM2O,
	},
	regular: {
		m2m: BuiltinBlockIds.InputListM2M,
		m2o: BuiltinBlockIds.InputSelectM2O,
		o2m: BuiltinBlockIds.InputListO2M,
		o2o: BuiltinBlockIds.InputSelectM2O,
	},
} satisfies Record<RelationTargetKind, Partial<Record<RelationType, string>>>;

/** Display block matrix keyed by target collection kind. */
const RELATION_DISPLAY_BLOCKS = {
	file: BuiltinBlockIds.DisplayFileThumbnail,
	regular: BuiltinBlockIds.DisplayRelatedValues,
} satisfies Record<RelationTargetKind, string>;

/** Resolves conservative primitive presentation defaults from the full schema type. */
export function getPrimitiveFieldPresentationDefaults(
	type: PrimitiveType,
): PrimitiveFieldPresentationDefaults {
	const constants =
		PRIMITIVE_FIELD_CONSTANTS[getTypename(type)]
		?? FALLBACK_PRIMITIVE_FIELD_CONSTANTS;
	const presentation = callOrReturn(constants.presentation, type);

	return structuredClone(presentation);
}

/**
 * Resolves the display block for a relation field. Unlike the input matrix, a display
 * block always exists for every relation kind — including ones with no supported input
 * block (e.g. a file o2m) — so this never throws and the field still renders. Used on the
 * read/display path, which must degrade gracefully rather than crash.
 */
export function getRelationFieldDisplayDefaults(
	field: ResolvedMonospaceSingleRelationField,
): FieldPresentationBlock {
	const targetKind: RelationTargetKind = isRelationToMonospaceFiles(field)
		? 'file'
		: 'regular';

	return { block: RELATION_DISPLAY_BLOCKS[targetKind] };
}

/** Resolves relation presentation defaults from target collection kind and relation cardinality. */
export function getRelationFieldPresentationDefaults(
	field: ResolvedMonospaceSingleRelationField,
): RelationFieldPresentationDefaults {
	const targetKind: RelationTargetKind = isRelationToMonospaceFiles(field)
		? 'file'
		: 'regular';
	const relationType = inferRelationType(field);
	const inputBlock = RELATION_INPUT_BLOCKS[targetKind][relationType];

	if (!inputBlock) {
		throw new Error(
			`No relation input block configured for ${targetKind} ${relationType}`,
		);
	}

	return structuredClone({
		input: {
			block: inputBlock,
		},
		display: {
			block: RELATION_DISPLAY_BLOCKS[targetKind],
		},
	});
}
